import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type ReactNode,
} from "react"
import type {
  ColumnDef,
  SortingState,
  GroupingState,
  ColumnSizingState,
  VisibilityState,
  Row,
  OnChangeFn,
} from "@tanstack/react-table"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import { useIsMultiSortEnabled } from "@/v2-backoffice-client/settings/settings.config"
import { buildQueryParams } from "./table.utils"
import {
  applyAdvancedFilter,
  buildRowValueGetter,
  pruneGroup,
} from "./filter/tableAdvancedFilter.utils"
import {
  TableProvider,
  type TablePanel,
  type RowActionsConfig,
} from "./table.context"
import type { FilterGroup, PageInfo, TableQueryParams } from "./table.types"
import { ACTIONS_COLUMN_ID, SPACER_COLUMN_ID } from "./table.constants"
import {
  usePersistedInitialState,
  useStableValue,
  useTableStatePersistence,
} from "./table.state"
import TableRowActions from "./TableRowActions"
import TableSummaryProvider from "./summary/TableSummaryProvider"

const ROW_ACTIONS_COLUMN_SIZE = 40
const NOOP = () => {}

export type TableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData, unknown>[]
  getRowId?(row: TData, index: number): string
  className?: string
  children?: ReactNode
  initialSorting?: SortingState
  /**
   * Controlled sorting state. When provided (together with `onSortingChange`)
   * the caller owns sorting: the table renders exactly this state and routes
   * every sort interaction through `onSortingChange`. The table stays agnostic
   * about where the state lives — `useTableSortUrl` uses this to make sorting
   * bookmarkable. Leave undefined for the default uncontrolled behavior.
   */
  sorting?: SortingState
  /** Change handler for controlled sorting (see `sorting`). */
  onSortingChange?: OnChangeFn<SortingState>
  /** Column ids to group by on first render (requires `enableGrouping`). */
  initialGrouping?: GroupingState
  initialColumnSizing?: ColumnSizingState
  /**
   * Visibility overrides layered on top of the columns' own `defaultHidden`
   * flags — for a table that swaps its defaults at runtime (a view-mode
   * switch). A column that is simply hidden until asked for should say so
   * with `defaultHidden` in `createColumns` instead.
   */
  defaultColumnVisibility?: VisibilityState
  enableRowSelection?: boolean
  enableSorting?: boolean
  enableGrouping?: boolean
  /**
   * Enables client-side pagination. When true, the table only
   * renders one page at a time and exposes pagination controls via
   * `Table.Pagination`. Use this for purely client-loaded data
   * sets that are large enough to be expensive to render in one
   * pass. Server-paginated tables should leave this off and use
   * the existing `pageInfo` + `onFetchMore` props on `Table.Content`.
   */
  enablePagination?: boolean
  /** Initial page size when pagination is enabled. Defaults to 50. */
  initialPageSize?: number
  /**
   * Enables client-side infinite scroll for tables that load all
   * their data up-front. When true:
   *
   *  - The table only renders the first `infiniteChunkSize` rows
   *    initially. As the operator scrolls near the bottom,
   *    `Table.Content` calls back into the table and the visible
   *    slice grows by another chunk (no extra network).
   *  - Sorting / filtering / grouping still operate over the *full*
   *    `data` array; only the rendered slice is capped, so the
   *    operator never sees a "page" of stale rows after sorting.
   *  - Sort or filter changes reset the slice back to one chunk so
   *    the user lands on the most relevant rows after re-querying.
   *
   * Mutually exclusive with `enablePagination` -- pick one. Server-
   * paginated tables (manualSorting / manualFiltering with a real
   * `pageInfo`) should keep both off and pass `pageInfo` /
   * `onFetchMore` to `Table.Content` directly.
   */
  enableInfiniteScroll?: boolean
  /** Chunk size used by infinite scroll. Defaults to 50. */
  infiniteChunkSize?: number
  manualSorting?: boolean
  manualFiltering?: boolean
  sortDescFirst?: boolean
  /** Whether cells should wrap text by default. Can be overridden per-column via `isWrapped` in createColumns. Defaults to false. */
  isCellsWrapped?: boolean
  /**
   * Virtual column name sent to the server when the global search input has a value.
   * Maps `globalFilter` to `{ column: searchColumn, contains: value }` in the query params.
   */
  searchColumn?: string
  /**
   * Unique identifier for this table instance. When provided, enables:
   * - Column resizing (drag header edges)
   * - Column reordering (drag headers)
   * - Persistence of column sizing, order, and visibility to localStorage
   * - A "Reset layout" option in the toolbar menu
   */
  tableId?: string
  onRowClick?(row: Row<TData>): void
  /**
   * Rows currently being written by a background operation (matched against
   * the same ids `getRowId` produces). A busy row shows a spinner in place
   * of its selection checkbox and cannot be (de)selected, so in-flight work
   * is visible directly in the table instead of the UI looking frozen.
   */
  busyRowIds?: ReadonlySet<string>
  /**
   * Id of the row whose inline detail panel is expanded (matched against
   * the same ids `getRowId` produces). When set together with
   * `renderRowDetail`, the table renders a full-width detail row directly
   * beneath the matching row. Purely additive: tables that pass neither
   * render exactly as before.
   */
  expandedRowId?: string | null
  /**
   * Renders the inline detail panel for the expanded row (see
   * `expandedRowId`). Called only for the matching row.
   */
  renderRowDetail?(row: Row<TData>): ReactNode
  /**
   * Per-row predicate that controls which row is visually highlighted
   * (e.g. the row whose view sheet is currently open).
   */
  getHighlightedRowId?(row: Row<TData>): boolean
  /**
   * Fires when sort or filter state changes. Provides query-ready params
   * (sortBy, sortDirection, filters) that can be spread into a GraphQL input.
   */
  onQueryChange?(params: TableQueryParams): void
}

function Table<TData>(props: TableProps<TData>) {
  const {
    data,
    columns,
    getRowId,
    className,
    children,
    initialSorting = [],
    sorting: controlledSorting,
    onSortingChange,
    initialGrouping,
    initialColumnSizing = {},
    defaultColumnVisibility,
    enableRowSelection = true,
    enableSorting = true,
    enableGrouping = false,
    enablePagination = false,
    initialPageSize = 50,
    enableInfiniteScroll = false,
    infiniteChunkSize = 50,
    manualSorting = false,
    manualFiltering = false,
    sortDescFirst = true,
    isCellsWrapped = false,
    searchColumn,
    tableId,
    onRowClick,
    busyRowIds,
    expandedRowId = null,
    renderRowDetail,
    getHighlightedRowId,
    onQueryChange,
  } = props

  const callbacksRef = useRef({ onRowClick, onQueryChange })
  callbacksRef.current = { onRowClick, onQueryChange }

  const rowToolbarRef = useRef(null)
  const rowActionsRef = useRef<RowActionsConfig | null>(null)
  const lastSelectedRowIdRef = useRef<string | null>(null)

  const [activePanel, setActivePanel] = useState<TablePanel>(null)
  const [advancedFilter, setAdvancedFilter] = useState<FilterGroup | null>(null)
  // "Showing X of Y" facts, synced up from Table.Content for the toolbar's
  // inline count. Primitive-compared so the sync effect can't loop.
  const [countMeta, setCountMeta] = useState<{
    total: number | null
    label: string
  }>({ total: null, label: "rows" })
  function updateCountMeta(meta: { total: number | null; label: string }) {
    setCountMeta((prev) =>
      prev.total === meta.total && prev.label === meta.label ? prev : meta,
    )
  }

  // Prioritised multi-column sorting is opt-in (Settings → Table): off by
  // default so a header click simply re-sorts by that column, which is what
  // most operators expect. When enabled, every click appends another sort tier.
  const isMultiSortEnabled = useIsMultiSortEnabled()

  const hasRowActions = hasChildOfType(children, TableRowActions)

  const effectiveColumns = useMemo(() => {
    const cols = [...columns]

    if (hasRowActions) {
      cols.push({
        id: ACTIONS_COLUMN_ID,
        size: ROW_ACTIONS_COLUMN_SIZE,
        minSize: ROW_ACTIONS_COLUMN_SIZE,
        maxSize: ROW_ACTIONS_COLUMN_SIZE,
        enableSorting: false,
        enableHiding: false,
        enableGrouping: false,
        enableResizing: false,
        header: () => null,
        cell(info) {
          return rowActionsRef.current?.render(info.row.original) ?? null
        },
      } satisfies ColumnDef<TData, unknown>)
    }

    if (tableId) {
      cols.push({
        id: SPACER_COLUMN_ID,
        enableSorting: false,
        enableHiding: false,
        enableGrouping: false,
        enableResizing: false,
        header: () => null,
        cell: () => null,
      } satisfies ColumnDef<TData, unknown>)
    }

    return cols
  }, [columns, hasRowActions, tableId])

  const columnIds = useMemo(
    () => effectiveColumns.map((c) => c.id!).filter(Boolean),
    [effectiveColumns],
  )

  // Virtual columns (filter/grouping carriers) are forced invisible in
  // every code path that writes visibility — including persisted user state
  // from before a column became virtual.
  const virtualHidden = useMemo(() => {
    const map: VisibilityState = {}
    for (const col of effectiveColumns) {
      const meta = col.meta as { virtual?: boolean } | undefined
      if (meta?.virtual && col.id) map[col.id] = false
    }
    return map
  }, [effectiveColumns])

  // Columns marked `defaultHidden` in createColumns start hidden. Read off
  // the definitions rather than restated per call site, so the flag works
  // wherever it's set and "Reset" in the columns panel returns to it.
  // Skipped for a column that can't be toggled back on, which would
  // otherwise be hidden with no way to reach it.
  const metaHidden = useMemo(() => {
    const map: VisibilityState = {}
    for (const col of effectiveColumns) {
      const meta = col.meta as { defaultHidden?: boolean } | undefined
      if (meta?.defaultHidden && col.id && col.enableHiding !== false) {
        map[col.id] = false
      }
    }
    return map
  }, [effectiveColumns])

  // The visibility a table falls back to: its column defaults, then what
  // the call site overrides per view mode, then the virtual columns nobody
  // may show. Anything the operator toggles layers on top of this. Held
  // stable by value, since it is derived from the column array and every
  // consumer below compares it by identity.
  const effectiveDefaultVisibility = useStableValue({
    ...metaHidden,
    ...defaultColumnVisibility,
    ...virtualHidden,
  })

  const persistedInitial = usePersistedInitialState({
    tableId,
    defaultColumnSizing: initialColumnSizing,
    defaultColumnVisibility: effectiveDefaultVisibility,
    columnIds,
  })

  const userColumnVisibilityRef = useRef<VisibilityState>(
    persistedInitial.userColumnVisibility,
  )

  // Slice cap used when infinite scroll is on. Grows by one chunk
  // each time `Table.Content` calls `onFetchMore`. Reset to one chunk
  // whenever sort / filter / search changes so the user lands on the
  // most relevant rows -- not page 7 of yesterday's view.
  const [infiniteSliceSize, setInfiniteSliceSize] = useState(infiniteChunkSize)

  // Pick the table's internal pagination size. `enableInfiniteScroll`
  // wins over `enablePagination` if both are accidentally passed.
  let internalPageSize = initialPageSize
  if (enableInfiniteScroll) internalPageSize = infiniteSliceSize
  const usesPagination = enablePagination || enableInfiniteScroll

  // Client-side tables (!manualFiltering) evaluate the advanced AND/OR/NOT
  // filter tree here, before TanStack's column filters / grouping / sorting run,
  // so all dimensions compose. Server tables emit the tree as `filterTree`
  // (onQueryChange) instead and leave the rows untouched.
  const rowValueGetter = useMemo(() => buildRowValueGetter(columns), [columns])
  const filteredData = useMemo(() => {
    if (manualFiltering || !advancedFilter) return data
    const pruned = pruneGroup(advancedFilter)
    if (!pruned) return data
    return applyAdvancedFilter(data, pruned, rowValueGetter)
  }, [data, manualFiltering, advancedFilter, rowValueGetter])

  const table = useReactTable({
    data: filteredData,
    columns: effectiveColumns,
    getRowId,
    defaultColumn: { minSize: 40 },
    initialState: {
      sorting: initialSorting,
      // Only set when given: an explicit `grouping: undefined` key would
      // override TanStack's default [] and crash every grouping.length read.
      ...(initialGrouping ? { grouping: initialGrouping } : {}),
      columnSizing: persistedInitial.columnSizing,
      columnOrder: persistedInitial.columnOrder,
      columnVisibility: {
        ...persistedInitial.columnVisibility,
        ...virtualHidden,
      },
      expanded: true,
      ...(usesPagination
        ? { pagination: { pageIndex: 0, pageSize: internalPageSize } }
        : {}),
    },
    state:
      enableInfiniteScroll || controlledSorting
        ? {
            ...(enableInfiniteScroll
              ? { pagination: { pageIndex: 0, pageSize: infiniteSliceSize } }
              : {}),
            ...(controlledSorting ? { sorting: controlledSorting } : {}),
          }
        : undefined,
    // Conditional spread on purpose: an explicit `onSortingChange: undefined`
    // key would clobber TanStack's built-in state updater and freeze sorting
    // on every uncontrolled table.
    ...(onSortingChange ? { onSortingChange } : {}),
    enableRowSelection,
    enableSorting,
    enableMultiSort: isMultiSortEnabled,
    isMultiSortEvent: isMultiSortEnabled ? () => true : undefined,
    sortDescFirst,
    enableGrouping,
    enableColumnResizing: !!tableId,
    columnResizeMode: "onChange",
    manualSorting,
    manualFiltering,
    meta: { busyRowIds },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: enableGrouping ? getExpandedRowModel() : undefined,
    getPaginationRowModel: usesPagination ? getPaginationRowModel() : undefined,
  })

  // Synthetic pageInfo + fetchMore for infinite scroll, exposed via
  // context so `Table.Content` can drive its scroll handler without
  // every consumer having to thread state through their own props.
  // The "next page exists" check is over the *filtered* row count so
  // empty filter results correctly stop the scroll loop.
  const filteredRowCount = enableInfiniteScroll
    ? table.getFilteredRowModel().rows.length
    : 0
  const infinitePageInfo: PageInfo | null = enableInfiniteScroll
    ? {
        totalCount: filteredRowCount,
        hasNextPage: filteredRowCount > infiniteSliceSize,
        hasPreviousPage: false,
      }
    : null

  const onInfiniteFetchMore = useCallback(() => {
    setInfiniteSliceSize((prev) => prev + infiniteChunkSize)
  }, [infiniteChunkSize])

  // Reset the visible slice when the operator changes sort or filters
  // so they don't end up halfway through a re-sorted list.
  const sortingKey = JSON.stringify(table.getState().sorting)
  const filtersKey = JSON.stringify(table.getState().columnFilters)
  const globalKey = String(table.getState().globalFilter ?? "")
  const advancedKey = JSON.stringify(advancedFilter)
  useEffect(() => {
    if (!enableInfiniteScroll) return
    setInfiniteSliceSize(infiniteChunkSize)
  }, [
    enableInfiniteScroll,
    infiniteChunkSize,
    sortingKey,
    filtersKey,
    globalKey,
    advancedKey,
  ])

  // Sync column visibility when the defaults change (a view-mode switch).
  // Preserves user-explicit overrides; only columns without overrides follow
  // the new defaults. Virtual columns stay hidden no matter what.
  const prevDefaultVisibilityRef = useRef(effectiveDefaultVisibility)
  useEffect(() => {
    const prev = prevDefaultVisibilityRef.current
    prevDefaultVisibilityRef.current = effectiveDefaultVisibility
    if (prev === effectiveDefaultVisibility) return

    const effective: VisibilityState = {
      ...effectiveDefaultVisibility,
      ...userColumnVisibilityRef.current,
      ...virtualHidden,
    }
    table.setColumnVisibility(effective)
  }, [effectiveDefaultVisibility, virtualHidden, table])

  const updateUserColumnVisibility = useCallback((update: VisibilityState) => {
    userColumnVisibilityRef.current = {
      ...userColumnVisibilityRef.current,
      ...update,
    }
  }, [])

  const clearUserColumnVisibility = useCallback(() => {
    userColumnVisibilityRef.current = {}
  }, [])

  const { resetState } = useTableStatePersistence({
    tableId,
    table,
    defaultColumnSizing: initialColumnSizing,
    defaultColumnVisibility: effectiveDefaultVisibility,
    userColumnVisibilityRef,
  })

  const hasPersistence = !!tableId
  const resetTableState = useCallback(() => {
    resetState()
  }, [resetState])

  // Clears every dataset-shaping dimension (column filters, search box,
  // advanced filter tree, grouping, sorting, pagination) in one shot — the
  // generic toolbar "reset" action. Layout (column sizing/order/visibility) is
  // intentionally left alone; that's what resetTableState is for.
  const resetQueryState = useCallback(() => {
    table.resetColumnFilters()
    table.resetGlobalFilter()
    table.resetGrouping()
    table.resetSorting()
    table.resetPagination()
    setAdvancedFilter(null)
  }, [table])

  // Emit onQueryChange when sort, filter, global search, or advanced
  // filter state changes
  const { sorting, columnFilters, globalFilter } = table.getState()
  const prevSortingRef = useRef(sorting)
  const prevFiltersRef = useRef(columnFilters)
  const prevGlobalFilterRef = useRef(globalFilter)
  const prevAdvancedFilterRef = useRef(advancedFilter)

  useEffect(() => {
    if (
      prevSortingRef.current === sorting &&
      prevFiltersRef.current === columnFilters &&
      prevGlobalFilterRef.current === globalFilter &&
      prevAdvancedFilterRef.current === advancedFilter
    ) {
      return
    }

    prevSortingRef.current = sorting
    prevFiltersRef.current = columnFilters
    prevGlobalFilterRef.current = globalFilter
    prevAdvancedFilterRef.current = advancedFilter

    table.resetRowSelection()
    const allColumns = table.getAllColumns()
    const params = buildQueryParams(sorting, columnFilters, allColumns)

    // Append globalFilter as a virtual column filter for the server
    if (searchColumn && globalFilter) {
      const filters = params.filters ?? []
      filters.push({ column: searchColumn, contains: String(globalFilter) })
      params.filters = filters
    }

    // Attach the advanced filter tree (server ANDs it with `filters`).
    if (advancedFilter) {
      params.filterTree = advancedFilter
    }

    callbacksRef.current.onQueryChange?.(params)
  }, [
    sorting,
    columnFilters,
    globalFilter,
    advancedFilter,
    table,
    searchColumn,
  ])

  return (
    <TableProvider
      value={{
        table,
        callbacksRef,
        expandedRowId,
        renderRowDetail: renderRowDetail ?? null,
        getHighlightedRowId: getHighlightedRowId ?? null,
        rowToolbarRef,
        rowActionsRef,
        lastSelectedRowIdRef,
        activePanel,
        setActivePanel,
        advancedFilter,
        setAdvancedFilter,
        isCellsWrapped,
        hasPersistence,
        defaultColumnVisibility: effectiveDefaultVisibility,
        resetTableState: hasPersistence ? resetTableState : NOOP,
        resetQueryState,
        updateUserColumnVisibility,
        clearUserColumnVisibility,
        infiniteScrollPageInfo: infinitePageInfo,
        infiniteScrollFetchMore: enableInfiniteScroll
          ? onInfiniteFetchMore
          : null,
        countMeta,
        setCountMeta: updateCountMeta,
      }}
    >
      <TableSummaryProvider tableId={tableId}>
        <div className={cn("ui-table-container", className)}>{children}</div>
      </TableSummaryProvider>
    </TableProvider>
  )
}

function hasChildOfType(children: ReactNode, type: unknown): boolean {
  let found = false
  React.Children.forEach(children, (child) => {
    if (found) return
    if (React.isValidElement(child) && child.type === type) {
      found = true
    }
  })
  return found
}

export default Table
